import { ReactNode, useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import { labelOf } from '../../../utils/labels';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { rupeesInWords } from '../../../lib/pricing';
import { Button } from '../../ui/Button/Button';
import { Card, CardHeader, Badge } from '../../ui/Display/Display';
import { DatePicker } from '../../ui/DatePicker/DatePicker';
import { Modal } from '../../ui/Modal/Modal';
import { Textarea } from '../../ui/Textarea/Textarea';
import type { LabelMap, StatusMap } from '../../../types/common';

/** Badge for any status map from lib/constants ({ key: [label, tone] }). */
export function StatusBadge({ map, value, className }: { map: StatusMap; value?: string | null; className?: string }) {
  const entry = value == null ? undefined : map[value];
  return (
    <Badge tone={entry?.[1] ?? 'neutral'} dot className={className}>
      {entry?.[0] ?? value}
    </Badge>
  );
}

/** Card with a titled header and a 2-column form grid. */
export function FormSection({ title, subtitle, action, children, className, bodyClassName }: { title?: ReactNode; subtitle?: ReactNode; action?: ReactNode; children?: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <Card className={className}>
      <CardHeader title={title} subtitle={subtitle} action={action} className="border-b border-line pb-4" />
      <div className={cn('grid gap-4 p-5 sm:grid-cols-2', bodyClassName)}>{children}</div>
    </Card>
  );
}

/** Card whose body is edge-to-edge (tables, line editors). */
export function TableSection({ title, subtitle, action, children, className }: { title?: ReactNode; subtitle?: ReactNode; action?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader title={title} subtitle={subtitle} action={action} className="pb-4" />
      {children}
    </Card>
  );
}

/** Main column + ~340px side column on wide screens. Side column comes first on phones when `asideFirst`. */
export function TwoColumn({ main, aside, asideFirst }: { main: ReactNode; aside?: ReactNode; asideFirst?: boolean }) {
  return (
    <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
      <div className={cn('min-w-0 space-y-5', asideFirst && 'order-2 xl:order-1')}>{main}</div>
      <aside className={cn('space-y-5 xl:sticky xl:top-24', asideFirst && 'order-1 xl:order-2')}>{aside}</aside>
    </div>
  );
}

/** Label / value rows inside a card. rows: [[label, value, { strong, tone }]] */
export type KeyValueRow = readonly [label: ReactNode, value: ReactNode, opts?: { strong?: boolean; tone?: string }];

/** Falsy rows are skipped, so rows can be written as `cond && [label, value]`. */
export function KeyValues({ rows, className }: { rows: (KeyValueRow | false | null | undefined | '' | 0)[]; className?: string }) {
  return (
    <dl className={cn('space-y-2.5 text-sm', className)}>
      {(rows.filter(Boolean) as KeyValueRow[]).map(([label, value, opts = {}], i) => (
        <div key={typeof label === 'string' ? label : i} className="flex items-start justify-between gap-4">
          <dt className="text-ink-500">{label}</dt>
          <dd className={cn('tabular text-right', opts.strong ? 'font-semibold text-navy-900' : 'font-medium text-navy-900', opts.tone)}>{value ?? '-'}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Subtotal / CGST / SGST / total box. `lead` rows go above the subtotal (e.g. labour + material split). */
export function TotalsBox({ totals, isGst = true, lead = [], className, label = 'Total payable' }: { totals: any; isGst?: boolean; lead?: KeyValueRow[]; className?: string; label?: ReactNode }) {
  const rows: (KeyValueRow | false | null)[] = [
    ...lead,
    ['Sub total', money(totals.subTotal)],
    isGst && ['CGST', money(totals.cgst)],
    isGst && ['SGST', money(totals.sgst)],
    totals.igst ? ['IGST', money(totals.igst)] : null,
    totals.roundOff ? ['Round off', money(totals.roundOff)] : null,
  ];
  return (
    <div className={cn('space-y-3', className)}>
      <KeyValues rows={rows} />
      <div className="rounded-lg bg-brand-50 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-navy-900">{label}</span>
          <span className="tabular text-xl font-semibold text-brand-700">{money(totals.grandTotal)}</span>
        </div>
        <p className="mt-1 text-xs text-ink-500">{rupeesInWords(totals.grandTotal)}</p>
      </div>
    </div>
  );
}

/** Timeline of statusHistory entries (newest first). */
export function HistoryCard({ history = [], statusMap, title = 'History' }: { history?: any[]; statusMap?: StatusMap | LabelMap; title?: ReactNode }) {
  const items = [...history].reverse();
  return (
    <Card>
      <CardHeader title={title} />
      <ol className="space-y-4 p-5 pt-4">
        {items.length === 0 && <li className="text-sm text-ink-400">Nothing yet.</li>}
        {items.map((h, i) => (
          <li key={`${h.status}-${h.at}-${i}`} className="relative flex gap-3">
            <span className={cn('mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full', i === 0 ? 'bg-brand-600 ring-4 ring-brand-100' : 'bg-navy-200')} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-navy-900">{statusMap ? labelOf(statusMap, h.status) : h.status}</p>
              <p className="text-xs text-ink-400">
                {h.at ? new Date(h.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </p>
              {h.note && <p className="mt-0.5 text-[13px] text-ink-500">{h.note}</p>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** Asks for a short reason before cancelling or reversing something. */
export function ReasonDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', loading }: { open: boolean; onClose: () => void; onConfirm: (reason: string) => void; title?: ReactNode; message?: ReactNode; confirmLabel?: string; loading?: boolean }) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const ok = reason.trim().length >= 3;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Keep it</Button>
          <Button variant="danger" onClick={() => onConfirm(reason.trim())} loading={loading} disabled={!ok}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <p className="mb-4 text-sm text-ink-600">{message}</p>}
      <Textarea label="Reason" required rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus placeholder="At least 3 letters" maxLength={300} />
    </Modal>
  );
}

/** From / To date pair for lists and reports. */
export function DateRange({ from, to, onChange, className }: { from: string; to: string; onChange: (patch: { from?: string; to?: string }) => void; className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 gap-2', className)}>
      <DatePicker size="sm" aria-label="From date" placeholder="From" value={from} max={to || undefined} onChange={(v) => onChange({ from: v ?? '', to })} />
      <DatePicker size="sm" aria-label="To date" placeholder="To" value={to} min={from || undefined} onChange={(v) => onChange({ from, to: v ?? '' })} />
    </div>
  );
}

/** Small "Created 29 Sept 2026" style meta line item with an icon-less dot separator. */
export const metaDate = (d?: string | Date | null) => (d ? formatDate(d) : '-');
